import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { supabase } from '../../services/supabase';
import { ChevronDown, Mail, ShieldCheck, TrendingDown, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PLANS = ['Free', 'Pro', 'Premium', 'Elite'];

const EMAIL_TEMPLATES = [
    { id: 'plan_upgraded', label: '🎉 Plan Upgraded' },
    { id: 'plan_expired', label: '⚠️ Plan Expired' },
    { id: 'payment_received', label: '💳 Payment Received' },
    { id: 'welcome', label: '👋 Welcome' },
    { id: 'custom', label: '✍️ Custom Message' },
];

export const AdminUsers: React.FC = () => {
    const [users, setUsers] = useState<any[]>([]);
    const [paymentRequests, setPaymentRequests] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'users' | 'payments'>('users');
    const [selectedPlans, setSelectedPlans] = useState<Record<string, string>>({});

    // Email modal state
    const [emailModal, setEmailModal] = useState<{ user: any } | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState('plan_upgraded');
    const [customSubject, setCustomSubject] = useState('');
    const [customMessage, setCustomMessage] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [emailStatus, setEmailStatus] = useState('');

    useEffect(() => {
        fetchUsers();
        fetchPaymentRequests();
    }, []);

    const fetchUsers = async () => {
        const { data } = await supabase
            .from('user_profiles')
            .select('*')
            .order('created_at', { ascending: false });
        if (data) {
            setUsers(data);
            // Initialize selected plans from current DB values
            const plans: Record<string, string> = {};
            data.forEach(u => { plans[u.id] = u.tier || 'Free'; });
            setSelectedPlans(plans);
        }
        setLoading(false);
    };

    const fetchPaymentRequests = async () => {
        const { data } = await supabase
            .from('payment_requests')
            .select('*')
            .order('submitted_at', { ascending: false });
        if (data) setPaymentRequests(data);
    };

    const upgradePlan = async (userId: string, plan: string) => {
        const { data: { user: adminUser } } = await supabase.auth.getUser();
        if (!adminUser) return;

        const { error } = await supabase.rpc('admin_change_subscription', {
            target_user_id: userId,
            new_plan_id: plan.toLowerCase(), // Maps 'Pro' -> 'pro', 'Elite' -> 'elite'
            admin_user_id: adminUser.id,
            admin_reason: `Admin manual change to ${plan}`,
            reset_quotas: true
        });

        if (error) {
            console.error('Plan upgrade error:', error);
            alert(`Plan update failed: ${error.message}`);
        }

        // Refresh anyway to keep UI in sync
        fetchUsers();
    };

    const approvePayment = async (requestId: string) => {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approve-payment`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`
            },
            body: JSON.stringify({ request_id: requestId, action: 'approve' })
        });
        const result = await res.json();
        if (result.success) {
            fetchPaymentRequests();
            fetchUsers();
        } else {
            alert('Approval failed: ' + result.error);
        }
    };

    const rejectPayment = async (requestId: string) => {
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approve-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` },
            body: JSON.stringify({ request_id: requestId, action: 'reject' })
        });
        const result = await res.json();
        if (result.success) fetchPaymentRequests();
    };

    const sendEmail = async () => {
        if (!emailModal) return;
        setEmailSending(true);
        setEmailStatus('');
        const user = emailModal.user;

        const payload: any = {
            to_email: user.email || user.id,
            to_name: user.email || 'Trader',
            template_name: selectedTemplate,
            variables: {
                plan_name: user.tier,
                custom_message: customMessage,
                expires_at: user.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString() : 'N/A'
            }
        };

        if (selectedTemplate === 'custom') {
            payload.custom_subject = customSubject;
            payload.custom_html = `<p>${customMessage.replace(/\n/g, '<br>')}</p>`;
        }

        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
            body: JSON.stringify(payload)
        });
        const result = await res.json();
        if (result.success) {
            setEmailStatus('✅ Email sent!');
            setTimeout(() => { setEmailModal(null); setEmailStatus(''); }, 1500);
        } else {
            setEmailStatus('❌ ' + result.error);
        }
        setEmailSending(false);
    };

    const tierBadge = (tier: string) => {
        const styles: Record<string, string> = {
            Elite: 'bg-yellow-500/20 text-yellow-400',
            Premium: 'bg-emerald-500/20 text-emerald-400',
            Pro: 'bg-blue-500/20 text-blue-400',
            Free: 'bg-gray-700 text-gray-400',
        };
        return <span className={`px-2 py-1 rounded text-xs font-bold ${styles[tier] || styles.Free}`}>{tier || 'Free'}</span>;
    };

    return (
        <AdminLayout>
            {/* Email Modal */}
            {emailModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md">
                        <h3 className="text-white font-bold text-lg mb-4">
                            Send Email to <span className="text-finance-accent">{emailModal.user.email || emailModal.user.id}</span>
                        </h3>

                        <div className="mb-4">
                            <label className="text-sm text-gray-400 mb-2 block">Choose Template</label>
                            <select
                                value={selectedTemplate}
                                onChange={e => setSelectedTemplate(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                            >
                                {EMAIL_TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                            </select>
                        </div>

                        {selectedTemplate === 'custom' && (
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Subject</label>
                                    <input value={customSubject} onChange={e => setCustomSubject(e.target.value)}
                                        placeholder="Email subject..." className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                                </div>
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Message Body</label>
                                    <textarea value={customMessage} onChange={e => setCustomMessage(e.target.value)}
                                        rows={5} placeholder="Write your message here..."
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white resize-none" />
                                </div>
                            </div>
                        )}

                        {emailStatus && <p className="text-sm mt-3">{emailStatus}</p>}

                        <div className="flex gap-3 mt-5">
                            <button onClick={sendEmail} disabled={emailSending}
                                className="flex-1 bg-finance-accent hover:bg-yellow-500 text-finance-darker font-bold py-2.5 rounded-lg flex items-center justify-center gap-2">
                                {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                Send
                            </button>
                            <button onClick={() => setEmailModal(null)}
                                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-2.5 rounded-lg">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-6">
                <h1 className="text-3xl font-bold text-white mb-2">User Management</h1>
                <p className="text-gray-400">Manage user plans, send emails, and review payment requests.</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
                {(['users', 'payments'] as const).map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                        className={`px-5 py-2 rounded-lg font-medium capitalize transition-colors ${activeTab === tab ? 'bg-finance-accent text-finance-darker' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>
                        {tab === 'payments' ? `Payment Requests ${paymentRequests.filter(r => r.status === 'pending').length > 0 ? `(${paymentRequests.filter(r => r.status === 'pending').length})` : ''}` : 'Users'}
                    </button>
                ))}
            </div>

            {/* USERS TAB */}
            {activeTab === 'users' && (
                <div className="bg-finance-card border border-gray-800 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-gray-400">
                            <thead className="bg-gray-800/50 text-gray-300">
                                <tr>
                                    <th className="px-5 py-4 font-medium">Email / ID</th>
                                    <th className="px-5 py-4 font-medium">Plan</th>
                                    <th className="px-5 py-4 font-medium">Status</th>
                                    <th className="px-5 py-4 font-medium">Expires</th>
                                    <th className="px-5 py-4 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {loading ? (
                                    <tr><td colSpan={5} className="px-5 py-8 text-center">Loading users...</td></tr>
                                ) : users.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-800/20 transition-colors">
                                        <td className="px-5 py-4 text-white text-xs font-mono">
                                            {user.email || user.id?.slice(0, 16) + '...'}
                                        </td>
                                        <td className="px-5 py-4">{tierBadge(user.tier)}</td>
                                        <td className="px-5 py-4">
                                            <span className={`text-xs ${user.subscription_status === 'active' ? 'text-emerald-400' : 'text-gray-500'}`}>
                                                {user.subscription_status || 'inactive'}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4 text-xs">
                                            {user.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString() : '—'}
                                        </td>
                                        <td className="px-5 py-4">
                                            <div className="flex gap-2 items-center">
                                                {/* Plan selector */}
                                                <select
                                                    value={selectedPlans[user.id] || user.tier || 'Free'}
                                                    onChange={e => {
                                                        const newPlan = e.target.value;
                                                        if (newPlan === (user.tier || 'Free')) return; // no change
                                                        setSelectedPlans(prev => ({ ...prev, [user.id]: newPlan }));
                                                        upgradePlan(user.id, newPlan);
                                                    }}
                                                    className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-white text-xs"
                                                >
                                                    {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                                                </select>
                                                {/* Email button */}
                                                <button
                                                    onClick={() => { setEmailModal({ user }); setSelectedTemplate('plan_upgraded'); setCustomMessage(''); setCustomSubject(''); }}
                                                    className="p-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/40 text-blue-400 transition-colors"
                                                    title="Send Email"
                                                >
                                                    <Mail className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* PAYMENTS TAB */}
            {activeTab === 'payments' && (
                <div className="bg-finance-card border border-gray-800 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-gray-400">
                            <thead className="bg-gray-800/50 text-gray-300">
                                <tr>
                                    <th className="px-5 py-4">Email</th>
                                    <th className="px-5 py-4">Plan</th>
                                    <th className="px-5 py-4">Amount</th>
                                    <th className="px-5 py-4">Duration</th>
                                    <th className="px-5 py-4">Status</th>
                                    <th className="px-5 py-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {paymentRequests.length === 0 ? (
                                    <tr><td colSpan={6} className="px-5 py-8 text-center">No payment requests yet.</td></tr>
                                ) : paymentRequests.map(req => (
                                    <tr key={req.id} className="hover:bg-gray-800/20 transition-colors">
                                        <td className="px-5 py-4 text-white text-xs">{req.user_email}</td>
                                        <td className="px-5 py-4">{tierBadge(req.plan_name)}</td>
                                        <td className="px-5 py-4 text-emerald-400 font-bold">${req.amount}</td>
                                        <td className="px-5 py-4">{req.duration_months} month(s)</td>
                                        <td className="px-5 py-4">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${req.status === 'approved' ? 'text-emerald-400 bg-emerald-500/10'
                                                : req.status === 'rejected' ? 'text-red-400 bg-red-500/10'
                                                    : 'text-yellow-400 bg-yellow-500/10'
                                                }`}>{req.status}</span>
                                        </td>
                                        <td className="px-5 py-4">
                                            {req.status === 'pending' && (
                                                <div className="flex gap-2">
                                                    <button onClick={() => approvePayment(req.id)}
                                                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-400 rounded-lg text-xs font-bold">
                                                        <CheckCircle className="w-3.5 h-3.5" /> Approve
                                                    </button>
                                                    <button onClick={() => rejectPayment(req.id)}
                                                        className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded-lg text-xs font-bold">
                                                        <XCircle className="w-3.5 h-3.5" /> Reject
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};
